// Team Users — tenant-side user management for domain_admin /
// office_manager / domain_supervisor. Currently focused on the one thing
// tenant managers kept escalating: resetting a teammate's password
// without going through Emergent/master admin. Strictly own-domain.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, KeyRound, Copy, ShieldCheck, X, Mail, CalendarOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { TeamScheduleDialog } from '../components/custom/TeamScheduleDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = new Set(['master_admin', 'domain_admin', 'domain_supervisor', 'office_manager']);

const ROLE_LABEL = {
  domain_admin: 'Admin',
  office_manager: 'Office Manager',
  domain_supervisor: 'Supervisor',
  domain_agent: 'Agent',
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 60 * 24 * 14) return `${Math.floor(mins / (60 * 24))}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function TeamUsersPage() {
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null); // user being reset
  const [emailOpt, setEmailOpt] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { name, temp_password, emailed, email_address, email_error }
  const [customPw, setCustomPw] = useState(''); // optional admin-chosen password

  const domainId = user?.domain_id;

  useEffect(() => {
    if (!token || !domainId) { setLoading(false); return; }
    axios.get(`${API}/admin/domains/${domainId}/users`, { headers })
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load users'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, domainId]);

  if (!MANAGER_ROLES.has(user?.role)) {
    return <div className="p-8 text-sm text-slate-500">You do not have access to this page.</div>;
  }
  if (user?.role === 'master_admin' && !domainId) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="team-users-master-hint">
        Master admins manage users per tenant in the <a href="/admin/domains" className="text-teal-600 underline">admin console</a> (open a domain → Users tab).
      </div>
    );
  }

  // Supervisors can't reset admin-level accounts — hide the button, matching
  // the backend rule (blocked, not just unwise).
  const canReset = (target) => {
    if (target.role === 'master_admin') return false;
    if (user.role === 'domain_supervisor' && ['domain_admin', 'office_manager'].includes(target.role)) return false;
    return true;
  };

  const doReset = async () => {
    if (!resetTarget) return;
    if (customPw && customPw.length < 8) { toast.error('Custom password must be at least 8 characters'); return; }
    setResetting(true);
    try {
      const r = await axios.post(
        `${API}/admin/domains/${domainId}/users/${resetTarget.id}/reset-password`,
        { email_to_user: emailOpt, email_address: emailOpt ? (emailAddr || null) : null, new_password: customPw || null }, { headers, timeout: 25000 },
      );
      setResetResult({
        name: resetTarget.name || resetTarget.email,
        temp_password: r.data.temp_password,
        emailed: r.data.emailed,
        email_address: r.data.email_address,
        email_error: r.data.email_error,
      });
      // Refresh audit columns (password_reset_by/at just changed)
      axios.get(`${API}/admin/domains/${domainId}/users`, { headers })
        .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
      setResetTarget(null);
      setCustomPw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // Per-user SMS access toggle — composes with the manager-role gate
  // on the backend; both must be true for Send SMS to work.
  const toggleSms = async (u) => {
    const next = !u.sms_access;
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/users/${u.id}/sms-access`,
        { sms_access: next }, { headers },
      );
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, sms_access: next } : x)));
      toast.success(`SMS access ${next ? 'enabled' : 'disabled'} for ${u.name || u.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update SMS access');
    }
  };

  // Per-user webphone approval + bulk (admins only — backend enforces)
  const togglePhone = async (u) => {
    const next = !u.phone_enabled;
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/users/${u.id}/phone-access`,
        { phone_enabled: next }, { headers },
      );
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, phone_enabled: next } : x)));
      toast.success(`Webphone ${next ? 'enabled' : 'disabled'} for ${u.name || u.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update phone access');
    }
  };
  const bulkPhone = async (enabled) => {
    try {
      const r = await axios.patch(
        `${API}/admin/domains/${domainId}/phone-access-bulk`,
        { phone_enabled: enabled }, { headers },
      );
      setUsers((prev) => prev.map((x) => ({ ...x, phone_enabled: enabled })));
      toast.success(`Webphone ${enabled ? 'enabled' : 'disabled'} for ${r.data.updated} users`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    }
  };

  const [tzInfo, setTzInfo] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  useEffect(() => {
    axios.get(`${API}/domain/settings`, { headers, params: domainId ? { domain_id: domainId } : {} })
      .then((r) => setTzInfo(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);
  const saveTz = async (tz) => {
    try {
      const r = await axios.patch(`${API}/domain/settings${domainId ? `?domain_id=${domainId}` : ''}`,
        { display_timezone: tz }, { headers });
      setTzInfo((p) => ({ ...p, display_timezone: r.data.display_timezone }));
      toast.success(`Domain timezone set to ${tz}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update timezone');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="team-users-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Team Users</h1>
          <p className="text-sm text-slate-500">
            Reset a teammate's password — they'll get a one-time temporary password and be asked to set their own on next login.
          </p>
        </div>
        {tzInfo && (
          <div className="text-right" data-testid="domain-timezone-setting">
            <label className="block text-[10px] uppercase font-bold text-slate-400 mb-0.5">Domain display timezone</label>
            <select value={tzInfo.display_timezone}
              onChange={(e) => saveTz(e.target.value)}
              className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="domain-timezone-select">
              {(tzInfo.options || []).map((o) => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
            </select>
            <p className="text-[10px] text-slate-400 mt-0.5">Used for quiet hours &amp; server-side times</p>
          </div>
        )}
      </header>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-teal-500" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b bg-slate-50">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5">Ext</th>
                <th className="px-4 py-2.5">Last login</th>
                <th className="px-4 py-2.5">Last password reset</th>
                <th className="px-4 py-2.5">SMS access</th>
                <th className="px-4 py-2.5">
                  <span className="block">Webphone</span>
                  <span className="flex gap-1 mt-0.5 normal-case tracking-normal">
                    <button onClick={() => bulkPhone(true)} className="text-[9px] font-semibold text-teal-600 hover:underline" data-testid="phone-bulk-enable">all on</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => bulkPhone(false)} className="text-[9px] font-semibold text-slate-400 hover:underline" data-testid="phone-bulk-disable">all off</button>
                  </span>
                </th>
                <th className="px-4 py-2.5 text-right">Password</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50/60" data-testid={`team-user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{u.name || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.email}</td>
                  <td className="px-4 py-2.5"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{u.extension || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500" data-testid={`last-login-${u.id}`}>
                    {fmtWhen(u.last_login_at) || <span className="text-slate-300">never</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500" data-testid={`last-reset-${u.id}`}>
                    {u.password_reset_at ? (
                      <span title={u.password_reset_at}>
                        {fmtWhen(u.password_reset_at)} <span className="text-slate-400">by {u.password_reset_by}</span>
                        {u.must_change_password && <span className="ml-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">temp pw active</span>}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`sms-access-cell-${u.id}`}>
                    {u.sms_toggle_allowed === false ? (
                      <span className="text-slate-300" title="Not in your delegation scope — an admin (or the agent's own supervisor) manages this switch.">—</span>
                    ) : (
                    <button
                      onClick={() => toggleSms(u)}
                      role="switch"
                      aria-checked={!!u.sms_access}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.sms_access ? 'bg-emerald-500' : 'bg-slate-200'}`}
                      data-testid={`sms-access-toggle-${u.id}`}
                      title={u.sms_access
                        ? 'SMS access ON — this user can send SMS from playbooks (manager roles only). Click to disable.'
                        : 'SMS access OFF — click to enable. Only takes effect for manager roles during the testing phase.'}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${u.sms_access ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                    )}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`phone-access-cell-${u.id}`}>
                    <button
                      onClick={() => togglePhone(u)}
                      role="switch"
                      aria-checked={!!u.phone_enabled}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.phone_enabled ? 'bg-teal-500' : 'bg-slate-200'}`}
                      data-testid={`phone-access-toggle-${u.id}`}
                      title={u.phone_enabled
                        ? 'Webphone ON — this user can sign into the embedded dialer. Click to disable.'
                        : 'Webphone OFF — click to let this user sign into the embedded dialer with their extension.'}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${u.phone_enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => setScheduleTarget(u)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 px-2 py-1.5 rounded-lg transition-colors mr-1"
                      title="Work days & leave"
                      data-testid={`schedule-btn-${u.id}`}
                    >
                      <CalendarOff className="w-3.5 h-3.5" /> Leave
                    </button>
                    {canReset(u) ? (
                      <button
                        onClick={() => {
                          const suggested = u.contact_email || (EMAIL_RE.test(u.email || '') ? u.email : '');
                          setResetTarget(u);
                          setEmailAddr(suggested);
                          setEmailOpt(!!suggested);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-900 hover:bg-teal-50 px-2.5 py-1.5 rounded-lg transition-colors"
                        data-testid={`reset-password-btn-${u.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5" /> Reset password
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No users found in this domain.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm dialog */}
      {resetTarget && (
        <div className="fixed inset-0 z-[90] bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="reset-confirm-dialog">
            <h3 className="font-bold text-slate-900">Reset password for {resetTarget.name || resetTarget.email}?</h3>
            <p className="text-xs text-slate-500">
              A secure temporary password will be generated and shown to you ONCE. Their current password stops working immediately, and they'll be required to set a new one on next login.
            </p>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Set a specific password (optional)</label>
              <input
                type="text"
                value={customPw}
                onChange={(e) => setCustomPw(e.target.value)}
                placeholder="Leave blank to auto-generate a secure one"
                className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="reset-custom-password"
              />
              {customPw && customPw.length < 8 && (
                <p className="text-[10px] text-amber-600 mt-0.5">Minimum 8 characters.</p>
              )}
            </div>
            {EMAIL_RE.test(resetTarget.email || '') || true ? (
              <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailOpt}
                    onChange={(e) => setEmailOpt(e.target.checked)}
                    className="mt-0.5 accent-teal-600"
                    data-testid="reset-email-checkbox"
                  />
                  <span>Also email the temporary password to the user (explains it's temporary and must be changed on login)</span>
                </label>
                {emailOpt && (
                  <input
                    type="email"
                    value={emailAddr}
                    onChange={(e) => setEmailAddr(e.target.value)}
                    placeholder="their.name@company.com"
                    className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="reset-email-address"
                  />
                )}
                {emailOpt && !EMAIL_RE.test(emailAddr) && (
                  <p className="text-[10px] text-amber-600">Enter a real email address — this user's login ID isn't a mailbox. It'll be saved for next time.</p>
                )}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <button onClick={() => setResetTarget(null)} className="text-sm px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="reset-cancel">Cancel</button>
              <button
                onClick={doReset}
                disabled={resetting}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-60"
                data-testid="reset-confirm"
              >
                {resetting && <Loader2 className="w-4 h-4 animate-spin" />} Reset password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-time temp password display */}
      {resetResult && (
        <div className="fixed inset-0 z-[90] bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="temp-password-dialog">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                <h3 className="font-bold text-slate-900">Temporary password ready</h3>
              </div>
              <button onClick={() => setResetResult(null)} className="text-slate-300 hover:text-slate-500" data-testid="temp-password-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-500">Share this with <strong>{resetResult.name}</strong> now — it is shown only once and they must change it on first login.</p>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <code className="flex-1 font-mono text-sm text-slate-800 break-all" data-testid="temp-password-value">{resetResult.temp_password}</code>
              <button
                onClick={() => { navigator.clipboard?.writeText(resetResult.temp_password); toast.success('Copied'); }}
                className="text-teal-600 hover:text-teal-800"
                data-testid="temp-password-copy"
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {resetResult.emailed && (
              <p className="text-xs text-emerald-600 flex items-center gap-1.5" data-testid="temp-password-emailed">
                <Mail className="w-3.5 h-3.5" /> Emailed to {resetResult.email_address}
              </p>
            )}
            {resetResult.email_error && (
              <p className="text-xs text-amber-600" data-testid="temp-password-email-error">
                Email not sent: {resetResult.email_error} — share the password directly.
              </p>
            )}
          </div>
        </div>
      )}
      {scheduleTarget && (
        <TeamScheduleDialog user={scheduleTarget} headers={headers} onClose={() => setScheduleTarget(null)} />
      )}
    </div>
  );
}
